import React, { useEffect, useImperativeHandle, useState } from 'react';
import {  Space, Spin, Tag } from 'antd';
import { CloseCircleFilled, CloseCircleOutlined, EditOutlined, ExclamationCircleOutlined, LockFilled } from '@ant-design/icons';
import './styles.less';
import classNames from 'classnames';

export const TAB_STATES = {
    COMPLETED: 'COMPLETED',
    LOCKED: 'LOCKED',
    REQUIRED: 'REQUIRED',
    ERROR: 'ERROR',
    UNSAVED: 'UNSAVED'
};

const STATUS_TAG = {
    [ TAB_STATES.ERROR ]: <Tag color='error' icon={ <CloseCircleOutlined /> }>Error</Tag>,
    [ TAB_STATES.UNSAVED ]: <Tag color='blue'>Unsaved Changes</Tag>,
    [ TAB_STATES.REQUIRED ]: <Tag color='warning' icon={ <ExclamationCircleOutlined /> }>Required</Tag>
};

export const TAB_ICONS = {
    [ TAB_STATES.REQUIRED ]: <ExclamationCircleOutlined style={ { color: '#faad14' } } />,
    [ TAB_STATES.ERROR ]: <CloseCircleFilled style={ { color: 'red' } } />,
    [ TAB_STATES.UNSAVED ]: <EditOutlined style={ { color: '#1677ff' } } />,
    [ TAB_STATES.LOCKED ]: <LockFilled style={ { color: 'grey' } } />,
};

const GenericTab = ( { ref, form, loading, statuses = [], parentError, onValidateCb, children, formNames, title, className } ) => {

    const [ errors, setErrors ] = useState( [] );
    
    useImperativeHandle( ref, () => ( {
        formFields: formNames,
        validate: () => validateTab(),
    } ) );

    function validateTab( initialValidation ) {
        form?.validateFields( formNames, { validateOnly: true, recursive: true } )
            .then( values => {
                let newStatus = [];
                if ( initialValidation !== true && form.isFieldsTouched( formNames ) ) {
                    newStatus.splice( 0, 0, TAB_STATES.UNSAVED );
                }
                if ( initialValidation === true && statuses.includes( TAB_STATES.REQUIRED ) ) {
                    newStatus.splice( 0, 0, TAB_STATES.REQUIRED );
                }
                setErrors();
                onValidateCb?.( newStatus );
            } )
            .catch( errors => {
                let newStatus = [];

                if ( initialValidation !== true ) {
                    setErrors( errors?.errorFields );
                }

                //TODO: unsaved, error, and maybe required should be gleaned from the form object (dirty, error, etc.)


                // i dont LOVE this logic because it requires that i use the word "required" in certain errors
                // this should not apply to just items with a required asterisk, they should be for those required to save the item
                if ( ( initialValidation === true && statuses.includes( TAB_STATES.REQUIRED ) ) || errors.errorFields.some( f => f.errors.some( e => e.toLowerCase().includes( 'required' ) ) ) ) {
                    newStatus.splice( 0, 0, TAB_STATES.REQUIRED );
                }
                if ( initialValidation !== true && errors.errorFields.some( f => f.errors.some( e => !e.toLowerCase().includes( 'required' ) ) ) ) {
                    newStatus.splice( 0, 0, TAB_STATES.ERROR );
                }
                if ( initialValidation !== true && form.isFieldsTouched( formNames ) ) {
                    newStatus.splice( 0, 0, TAB_STATES.UNSAVED );
                } 
                onValidateCb?.( newStatus );
            } );
    }

    function addStatus( status, forceUpdate = false ) {
        if ( statuses.includes( status ) ) {
            if ( forceUpdate ) {
                onValidateCb?.( statuses );
            }
            return;
        }
        onValidateCb?.( [ ...statuses, status ] );
    }

    function makeDirty() {
        addStatus( TAB_STATES.UNSAVED );
    }

    useEffect( () => {
        if ( parentError?.errorFields?.some( e => formNames.includes( e?.name?.[ 0 ] ) ) ) {
            addStatus( TAB_STATES.ERROR );
        }
    }, [ parentError ] );

    useEffect( () => {
        if ( !loading ) {
            validateTab( true );
        }
    }, [ loading ] );

    return <Space direction='vertical' className={ classNames( 'content', className ) }>
        <Space className='header-info'>
            <h3>{ title }</h3>
            { statuses.length > 0 && <Space>{ [ ...statuses ].sort().map( s => STATUS_TAG[ s ] ) }</Space> }
        </Space>
        { loading ? <Spin /> : typeof children == 'function' ?  children( { validateTab, errors, makeDirty } ) : children }
    </Space>;
};

export default GenericTab;